import { useMemo } from 'react'

const MEMBER_COLORS = {
  slate: '#64748b', rose: '#f43f5e', amber: '#f59e0b', emerald: '#10b981',
  sky: '#0ea5e9', violet: '#8b5cf6', pink: '#ec4899', orange: '#f97316',
  teal: '#14b8a6', indigo: '#6366f1',
}

function fmt(isoStr) {
  const d = new Date(isoStr)
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
}

export function ScheduleTodayPanel({ data }) {
  const { events = [], members = [] } = data
  const now = new Date()
  const isEvening = now.getHours() >= 18

  const targetDate = isEvening ? new Date(now.getTime() + 86400000) : now
  const dateStr = targetDate.toISOString().split('T')[0]

  const todayEvents = useMemo(() =>
    events.filter((e) => e.start_time?.startsWith(dateStr))
      .sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [events, dateStr]
  )

  const memberMap = useMemo(() =>
    Object.fromEntries(members.map((m) => [m.id, m])),
    [members]
  )

  const label = isEvening
    ? `Tomorrow — ${targetDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`
    : `Today — ${now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`

  return (
    <div className="flex flex-col h-full px-4 sm:px-8 py-4 sm:py-6 panel-animate-in min-h-0">
      <p className="text-gray-500 text-xs sm:text-sm font-semibold uppercase tracking-widest mb-1">Schedule</p>
      <h2 className="text-lg sm:text-2xl font-bold text-white mb-4 sm:mb-6">{label}</h2>

      {todayEvents.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="text-5xl sm:text-6xl mb-3">🌿</div>
          <p className="text-gray-400 text-lg sm:text-xl">Nothing scheduled {isEvening ? 'tomorrow' : 'today'}</p>
          <p className="text-gray-600 text-sm sm:text-base mt-1">Enjoy the free time!</p>
        </div>
      ) : (
        <div className="flex-1 space-y-2 overflow-y-auto min-h-0 scrollbar-hide">
          {todayEvents.map((event) => {
            const eventMembers = (event.event_members ?? []).map((em) => memberMap[em.user_id]).filter(Boolean)
            return (
              <div key={event.id} className="flex items-center gap-3 sm:gap-4 bg-white/8 rounded-xl sm:rounded-2xl px-4 py-3">
                <div className="text-right w-20 sm:w-24 flex-shrink-0">
                  <span className="text-white font-bold text-base sm:text-lg">
                    {event.is_all_day ? 'All day' : fmt(event.start_time)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-base sm:text-xl font-semibold truncate">{event.title}</p>
                  {event.location && <p className="text-gray-400 text-xs sm:text-sm mt-0.5 truncate">{event.location}</p>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {eventMembers.slice(0, 4).map((m) => (
                    <div
                      key={m.id}
                      className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: MEMBER_COLORS[m.memberColor] ?? '#8b5cf6' }}
                      title={m.displayName}
                    >
                      {m.displayName?.[0]?.toUpperCase()}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
